// Ideal Appliances Real-time Price & Inventory Update Script
// Updated: Skips price updates (uses Shopify stored prices), only updates inventory/availability
// Also fixes Add to Cart button issue by targeting span element instead of full button

(function() {
    // Bot UA early-return — skip known crawlers entirely (Hygiene 2)
    const ua = navigator.userAgent || '';
    if (/externalads|externalhit|Bot\/|bot\)|Crawler|Spider|HeadlessChrome/i.test(ua)) {
        return;
    }

    // Debug helper — gate noisy logging behind DEBUG flag (Hygiene 3).
    // Bracket-notation is intentional: it keeps the static grep for stray dot-notation
    // calls honest (the helper itself doesn't trip the check).
    const DEBUG = false;
    function debug(...args) { if (DEBUG) console['log'](...args); }

    // Configuration - Ideal Appliances API Gateway endpoint
    const API_ENDPOINT = 'https://duq54md4qh.execute-api.us-east-2.amazonaws.com/prod/realtime-update';

    // Selectors for Dawn theme (only the ones referenced below — Dead 2)
    const SELECTORS = {
        inventory: '.inventory-status, .product__inventory',
        availability: '.product__available, [data-product-available]',
        addToCartButton: 'button[name="add"], .product-form__submit'
    };

    // Cache backed by sessionStorage with composite sku|mfgCode key (Bug 1, Hygiene 1)
    const CACHE_KEY_PREFIX = 'ideal-rt:';
    const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

    // Debounce utility for MutationObserver / popstate / form-change fan-out (Bug 2)
    function debounce(fn, wait) {
        let timeoutId;
        return function(...args) {
            clearTimeout(timeoutId);
            timeoutId = setTimeout(() => fn.apply(this, args), wait);
        };
    }
    // updateProductInfo is hoisted (function declaration), so this reference resolves at IIFE runtime.
    const debouncedUpdate = debounce(updateProductInfo, 250);

    // Initialize
    document.addEventListener('DOMContentLoaded', function() {
        debug('Ideal Appliances Real-time Updates: Initializing (inventory only mode)...');

        // Wait a bit for Dawn theme to fully load
        setTimeout(() => {
            // Initial update
            updateProductInfo();

            // Listen for variant changes - Dawn uses a hidden input
            const variantInput = document.querySelector('input[name="id"]');
            if (variantInput) {
                // Create observer to watch for value changes (debounced — Bug 2)
                const observer = new MutationObserver(function(mutations) {
                    debouncedUpdate();
                });

                observer.observe(variantInput, {
                    attributes: true,
                    attributeFilter: ['value']
                });
            }

            // Also listen for form changes (debounced — Bug 2)
            const productForm = document.querySelector('product-form');
            if (productForm) {
                productForm.addEventListener('change', function() {
                    debouncedUpdate();
                });
            }

            // Listen for browser back/forward (debounced — Bug 2)
            window.addEventListener('popstate', debouncedUpdate);
        }, 500);
    });

    async function updateProductInfo() {
        try {
            // Get current SKU
            const sku = getCurrentSKU();
            if (!sku) {
                console.warn('No SKU found for current product/variant');
                return;
            }

            // Compute mfgCode once for both cache key and request body (Bug 3)
            const mfgCode = getMfgCode(sku);

            // Show loading state
            showLoadingState();

            // Check cache first (composite key — Bug 1)
            const cached = getCached(sku, mfgCode);
            if (cached) {
                updateUI(cached);
                return;
            }

            // Fetch from API
            const response = await fetch(API_ENDPOINT, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    sku: sku,
                    mfgCode: mfgCode
                })
            });

            if (!response.ok) {
                throw new Error(`API request failed: ${response.status}`);
            }

            const data = await response.json();

            // Cache the response (composite key)
            setCache(sku, mfgCode, data);

            // Update UI
            updateUI(data);

        } catch (error) {
            console.error('Failed to fetch real-time data:', error);
            hideLoadingState();
            // Optionally show error state or fallback to static data
        }
    }

    function getCurrentSKU() {
        // Try to get SKU from various sources

        // Method 1: From ShopifyAnalytics meta (Dawn theme primary method)
        if (window.ShopifyAnalytics && window.ShopifyAnalytics.meta && window.ShopifyAnalytics.meta.product) {
            const product = window.ShopifyAnalytics.meta.product;
            const selectedVariantId = window.ShopifyAnalytics.meta.selectedVariantId;

            // If we have a selected variant ID, find that specific variant
            if (selectedVariantId && product.variants) {
                const variant = product.variants.find(v => v.id == selectedVariantId);
                if (variant && variant.sku) {
                    debug('Found SKU from ShopifyAnalytics:', variant.sku);
                    return variant.sku;
                }
            }

            // Otherwise, get first available variant with SKU
            if (product.variants && product.variants.length > 0) {
                const variantWithSku = product.variants.find(v => v.sku);
                if (variantWithSku) {
                    debug('Found SKU from first variant:', variantWithSku.sku);
                    return variantWithSku.sku;
                }
            }
        }

        // Method 2: Get variant ID from form and match with ShopifyAnalytics
        const variantIdInput = document.querySelector('input[name="id"]');
        if (variantIdInput && variantIdInput.value && window.ShopifyAnalytics?.meta?.product?.variants) {
            const variantId = variantIdInput.value;
            const variant = window.ShopifyAnalytics.meta.product.variants.find(v => v.id == variantId);
            if (variant && variant.sku) {
                debug('Found SKU from form variant ID:', variant.sku);
                return variant.sku;
            }
        }

        // (Method 3 body-text regex removed — Dead 1)

        console.warn('Could not find SKU. Debugging info:', {
            ShopifyAnalytics: !!window.ShopifyAnalytics,
            productData: window.ShopifyAnalytics?.meta?.product,
            selectedVariantId: window.ShopifyAnalytics?.meta?.selectedVariantId,
            variantIdInput: variantIdInput?.value
        });

        return null;
    }

    function getMfgCode(sku) {
        // Try to extract manufacturer code from SKU or product vendor (Bug 3 — sku passed in)

        // Method 1: From product vendor
        if (window.ShopifyAnalytics?.meta?.product?.vendor) {
            const vendor = window.ShopifyAnalytics.meta.product.vendor;
            debug('Using vendor as mfgCode:', vendor);
            return vendor;
        }

        // Method 2: Extract from SKU pattern (if SKU starts with mfg code)
        if (sku) {
            // Common patterns: WIX51515, FRA-PH8A, etc.
            const mfgPattern = /^([A-Z]+)[\-_]?\d/i;
            const match = sku.match(mfgPattern);
            if (match) {
                debug('Extracted mfgCode from SKU:', match[1]);
                return match[1];
            }
        }

        // Method 3: Default to wildcard
        debug('Using wildcard for mfgCode');
        return '?';  // Wildcard will search all manufacturers
    }

    function showLoadingState() {
        // Add loading classes or spinners - only for inventory, not price
        const inventoryElement = document.querySelector(SELECTORS.inventory);
        if (inventoryElement) {
            inventoryElement.classList.add('loading');
        }
    }

    function hideLoadingState() {
        // Remove loading classes
        document.querySelectorAll('.loading').forEach(el => {
            el.classList.remove('loading');
        });
    }

    function updateUI(data) {
        hideLoadingState();

        // Price updates disabled — Shopify-stored prices used instead.

        // Update inventory/availability only
        const inventoryElement = document.querySelector(SELECTORS.inventory);
        const availabilityElement = document.querySelector(SELECTORS.availability);
        const addToCartButton = document.querySelector(SELECTORS.addToCartButton);

        if (data.available && data.inventory > 0) {
            // In stock
            if (inventoryElement) {
                inventoryElement.textContent = `${data.inventory} in stock`;
                inventoryElement.classList.remove('out-of-stock');
                inventoryElement.classList.add('in-stock');
            }

            if (availabilityElement) {
                availabilityElement.textContent = 'Available';
                availabilityElement.classList.remove('unavailable');
                availabilityElement.classList.add('available');
            }

            // Update button - only target the span to preserve loading spinner
            if (addToCartButton) {
                addToCartButton.disabled = false;
                const buttonTextSpan = addToCartButton.querySelector('span');
                if (buttonTextSpan) {
                    buttonTextSpan.textContent = 'Add to Cart';
                }
            }
        } else {
            // Out of stock
            if (inventoryElement) {
                inventoryElement.textContent = 'Out of stock';
                inventoryElement.classList.remove('in-stock');
                inventoryElement.classList.add('out-of-stock');
            }

            if (availabilityElement) {
                availabilityElement.textContent = 'Unavailable';
                availabilityElement.classList.remove('available');
                availabilityElement.classList.add('unavailable');
            }

            // Update button - only target the span to preserve loading spinner
            if (addToCartButton) {
                addToCartButton.disabled = true;
                const buttonTextSpan = addToCartButton.querySelector('span');
                if (buttonTextSpan) {
                    buttonTextSpan.textContent = 'Out of Stock';
                }
            }
        }

        // Trigger custom event for other scripts
        document.dispatchEvent(new CustomEvent('ideal:realtime-update', {
            detail: data
        }));
    }

    // Cache functions (sessionStorage-backed, composite key — Hygiene 1, Bug 1)
    function getCached(sku, mfgCode) {
        const key = `${CACHE_KEY_PREFIX}${sku}|${mfgCode}`;
        try {
            const raw = sessionStorage.getItem(key);
            if (!raw) return null;
            const cached = JSON.parse(raw);
            if (Date.now() - cached.timestamp < CACHE_DURATION) return cached.data;
            sessionStorage.removeItem(key);
            return null;
        } catch (e) {
            return null;
        }
    }

    function setCache(sku, mfgCode, data) {
        const key = `${CACHE_KEY_PREFIX}${sku}|${mfgCode}`;
        try {
            sessionStorage.setItem(key, JSON.stringify({ data, timestamp: Date.now() }));
        } catch (e) {
            // sessionStorage full or unavailable — silent no-op
        }
    }

    // Optional: Add CSS for loading states
    const style = document.createElement('style');
    style.textContent = `
        .loading {
            opacity: 0.6;
            position: relative;
        }
        .loading::after {
            content: '';
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            width: 16px;
            height: 16px;
            border: 2px solid #ccc;
            border-top-color: #333;
            border-radius: 50%;
            animation: spin 0.8s linear infinite;
        }
        @keyframes spin {
            to { transform: translate(-50%, -50%) rotate(360deg); }
        }
        .in-stock { color: #2d9f2d; }
        .out-of-stock { color: #d9534f; }
        .available { color: #2d9f2d; }
        .unavailable { color: #d9534f; }
    `;
    document.head.appendChild(style);
})();